import ogSalong from "../assets/og-salong.jpg";
import midSkinFadeSidaAsset from "../assets/mid-skin-fade-sida.webp.asset.json";

// Fallback share image: the legacy WordPress uploads are gone (they 200 with the
// app shell), so every article that pointed at /wp-content/ uses the salon photo.
const ogFallback = ogSalong;

// Blog articles. Slugs are top-level to match legacy WordPress URLs.

export const BLOG_CATEGORIES = [
  "Herrklippning",
  "Skägg & Mustasch",
  "Grooming & Hudvård",
  "Produkter & Stylingtips",
  "Inspiration från salongen",
] as const;

export type BlogCategory = (typeof BLOG_CATEGORIES)[number];

export type BlogBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "img"; src: string; alt: string }
  | { type: "figure"; src: string; alt: string; caption: string }
  | { type: "figures"; items: { src: string; alt: string; caption: string }[] };

export interface BlogArticle {
  slug: string;
  title: string;
  metaDescription: string;
  category: BlogCategory;
  publishedAt: string; // ISO date
  hero: string;
  excerpt: string;
  intro: string;
  faq: { q: string; a: string }[];
}

export const articles: BlogArticle[] = [
  {
    slug: "hur-ofta-bor-man-klippa-sig-hos-barberare",
    title: "Hur ofta bör man klippa sig hos barberare?",
    metaDescription:
      "Undrar du hur ofta du egentligen borde gå till barberaren? Läs hur ofta män vanligtvis klipper sig beroende på fade, skägg, hårtyp och vilken look man vill behålla.",
    category: "Herrklippning",
    publishedAt: "2026-03-24",
    hero: ogFallback,
    excerpt:
      "Riktlinjer för hur ofta du bör boka barberare — beroende på fade, klassisk klippning och skägg.",
    intro: "En av de vanligaste frågorna vi får är: Hur ofta ska man egentligen klippa sig?",
    faq: [
      {
        q: "Hur ofta bör män klippa sig hos barberare?",
        a: "Det beror på frisyr och hårväxt. En skinfade rekommenderas var 2–3 vecka, medan en klassisk herrklippning fungerar bra var 3–5 vecka.",
      },
      {
        q: "Hur ofta ska man klippa en skinfade?",
        a: "För att hålla den riktigt ren och skarp rekommenderas klippning var 2–3 vecka.",
      },
      {
        q: "Hur ofta bör man trimma skägget professionellt?",
        a: "Många väljer att forma och trimma skägget var 2–4 vecka för att behålla symmetri och tydlig käklinje.",
      },
      {
        q: "Är det dyrt att klippa sig varannan eller var tredje vecka?",
        a: "Ja, det innebär en högre total kostnad. Men för många handlar det mer om prioritering och hur man vill uppfattas.",
      },
      {
        q: "Vad är vanligast i Stockholm?",
        a: "Många män klipper sig var 3:e eller 4:e vecka. De som prioriterar en mycket skarp stil väljer ofta ett intervall på 2–3 veckor.",
      },
    ],
  },
  {
    slug: "herrfrisyrer-2026",
    title: "Herrfrisyrer 2026 – De populäraste frisyrerna för män",
    metaDescription:
      "Vilken herrfrisyr passar dig 2026? Se årets största hårtrender för män, populära fades, moderna klippningar och stylingtips från barberare i Stockholm.",
    category: "Herrklippning",
    publishedAt: "2026-03-04",
    hero: ogFallback,
    excerpt:
      "Årets största trender för män — textured crop, taper fade och flow haircut.",
    intro: "Herrfrisyrer 2026 – De populäraste frisyrerna för män",
    faq: [
      {
        q: "Vilka herrfrisyrer är populära 2026?",
        a: "Några av de mest populära herrfrisyrerna just nu är textured crop, taper fade, slick back och olika varianter av naturliga frisyrer med mer längd på toppen.",
      },
      {
        q: "Hur ofta bör män klippa sig?",
        a: "De flesta män klipper sig var tredje till sjätte vecka beroende på frisyr och hur snabbt håret växer.",
      },
      {
        q: "Vilken frisyr passar bäst för tunt hår?",
        a: "För tunt hår fungerar kortare frisyrer ofta bäst, exempelvis textured crop eller kort fade eftersom de skapar mer volym och struktur.",
      },
      {
        q: "Vad ska man säga till barberaren när man vill ha en ny frisyr?",
        a: "Det bästa är att visa en referensbild och beskriva hur kort du vill ha sidorna och hur mycket längd du vill behålla på toppen.",
      },
      {
        q: "Var kan man klippa sig hos barber i Stockholm City?",
        a: "Du kan boka tid hos Tailor's Barber inne på Åhléns City på Klarabergsgatan 50.",
      },
    ],
  },
  {
    slug: "ar-barberare-vart-priset-jamfort-med-vanlig-frisor",
    title: "Är barberare värt priset jämfört med vanlig frisör?",
    metaDescription:
      "Vad är egentligen skillnaden mellan en barberare och en vanlig frisör? Vi går igenom varför många män väljer barberare för en mer specialiserad upplevelse.",
    category: "Herrklippning",
    publishedAt: "2026-02-27",
    hero: ogFallback,
    excerpt:
      "Barberare vs frisör — när är specialiseringen värd priset?",
    intro: "Många ställer sig frågan:\nÄr det verkligen värt att betala mer för en barberare – när en vanlig frisör ofta är billigare?\n\nSvaret beror på vad du är ute efter.",
    faq: [
      {
        q: "Är en barberare bättre än en frisör för män?",
        a: "Inte nödvändigtvis bättre, men mer specialiserad. Om du vill ha en skarp fade, precision med maskin eller professionell skäggformning är en barberare ofta rätt val.",
      },
      {
        q: "Är barberare dyrare än frisör i Stockholm?",
        a: "Priserna ligger ofta på liknande nivå i Stockholm. Skillnaden handlar mer om inriktning och specialisering.",
      },
      {
        q: "Vem ska jag välja om jag har längre hår?",
        a: "Har du längre hår med mycket lager kan en frisör vara ett bra alternativ. För kortare herrfrisyrer och fades är en barberare mer specialiserad.",
      },
      {
        q: "Kan en barberare klippa längre hår?",
        a: "Ja, men deras främsta expertis är kortare herrfrisyrer, fades och skägg.",
      },
      {
        q: "Är barberare bättre på skägg än frisörer?",
        a: "Generellt sett ja. Barberare är utbildade i skäggtrimning, formning och rakning med fokus på symmetri och ansiktsform.",
      },
    ],
  },
  {
    slug: "nar-ar-drop-in-hos-barberare-i-stockholm-ett-bra-val",
    title: "När är drop-in hos barberare i Stockholm ett bra val?",
    metaDescription:
      "Fundera på att gå till en barberare på drop-in? Här går vi igenom när drop-in passar bäst, vilka tider som ofta är lugnare och hur du ökar chansen att få tid direkt.",
    category: "Inspiration från salongen",
    publishedAt: "2026-02-23",
    hero: ogFallback,
    excerpt:
      "När drop-in fungerar bäst — och när du bör boka i förväg istället.",
    intro: "Att boka tid i förväg är oftast det säkraste sättet att få exakt den tid du vill ha.\nMen ibland är drop-in det perfekta alternativet.",
    faq: [
      {
        q: "Hur fungerar drop-in hos en barberare?",
        a: "Drop-in innebär att du inte bokar tid i förväg utan kommer direkt till salongen. Du får hjälp i turordning när en barberare blir ledig.",
      },
      {
        q: "Är det lång väntetid på drop-in i Stockholm?",
        a: "Det beror på dag och tid. Förmiddagar på vardagar har ofta kortare väntetid, medan eftermiddagar, fredagar och lördagar kan vara mer belastade.",
      },
      {
        q: "Kan jag välja barberare vid drop-in?",
        a: "Det är inte alltid garanterat. Vill du boka en specifik barberare rekommenderas tidsbokning.",
      },
      {
        q: "Är drop-in billigare än att boka tid?",
        a: "Nej, priset är normalt detsamma. Drop-in handlar om flexibilitet – inte rabatt.",
      },
      {
        q: "Passar drop-in för skinfade eller avancerade klippningar?",
        a: "Ja, men om du vill göra en större förändring kan det vara tryggare att boka tid i förväg.",
      },
      {
        q: "Kan jag ringa innan jag kommer för drop-in?",
        a: "Ja, det är ofta en bra idé att ringa salongen för att få en uppskattning av väntetiden.",
      },
    ],
  },
  {
    slug: "basta-barberare-i-stockholm-2026",
    title: "Bästa barberare Stockholm 2026: så väljer du rätt | Tailor's Barber",
    metaDescription:
      "Bästa barberare Stockholm 2026? Tailor's Barber på Åhléns City, Täby, Åkersberga och Arninge. Herrklippning 450 kr, fade och skägg. Boka online.",
    category: "Herrklippning",
    publishedAt: "2025-11-14",
    hero: ogFallback,
    excerpt:
      "Vad utmärker bästa barberare Stockholm, och varför Tailor's Barber blivit ett populärt val i city.",
    intro: "Letar du efter bästa barberare Stockholm 2026? I den här guiden går vi igenom vad som utmärker en riktigt bra barberare och varför Tailor's Barber i Stockholm City blivit ett av de populäraste valen.",
    faq: [],
  },
  {
    slug: "misstag-vaxer-ut-haret",
    title: "Det största misstaget du gör när du växer ut håret – och hur du undviker det",
    metaDescription:
      "Låter du håret växa ut? Gör det på rätt sätt. Här berättar våra barberare vad du ska undvika – och hur du håller frisyren snygg under tiden.",
    category: "Herrklippning",
    publishedAt: "2025-09-29",
    hero: ogFallback,
    excerpt:
      "Så undviker du \"köttbulle-effekten\" när du växer ut håret — klipp smart istället för att sluta helt.",
    intro: "Du har bestämt dig: det är dags att spara ut håret. Du vill ha längd, kanske till och med en ny look helt och hållet. Så du slutar klippa dig. Helt.\nMen efter några månader ser det… fel ut. Puffigt, ojämnt, otydligt. Som en köttbulle.\nVi fattar – och vi vet varför det händer. Läs mer nedanför",
    faq: [],
  },
  {
    slug: "skaggbalsam-valvardat-skagg",
    title: "Skäggbalsam – Så använder du det för ett mjukt och välvårdat skägg",
    metaDescription:
      "Upptäck hur du använder skäggbalsam för ett mjukt, friskt och välvårdat skägg. Läs barberarens bästa tips från Tailor's Barber i Stockholm.",
    category: "Skägg & Mustasch",
    publishedAt: "2025-11-06",
    hero: ogFallback,
    excerpt:
      "Guide till skäggbalsam — så använder du det steg för steg för ett mjukt, formbart skägg.",
    intro: "Ett snyggt skägg handlar inte bara om längd och form – det handlar om hur du tar hand om det.\nOm du vill ha ett mjukt, friskt och välvårdat skägg är skäggbalsam en produkt du inte kan vara utan.\nHär förklarar barberarna på Tailor’s Barber varför skäggbalsam är ett måste i din groomingrutin – och hur du använder det på rätt sätt.",
    faq: [],
  },
  {
    slug: "barber-frisor",
    title: "Vad är skillnaden mellan barber och frisör?",
    metaDescription:
      "Skillnaden mellan barber och frisör? Vi förklarar vem som passar bäst för herrklippning, fades och skägg – och när en barbershop är rätt val.",
    category: "Inspiration från salongen",
    publishedAt: "2025-12-22",
    hero: ogFallback,
    excerpt:
      "Barber vs frisör — vad skiljer dem åt och när ska du välja vilket?",
    intro: "Många använder orden barber och frisör som om de betyder samma sak – men det finns tydliga skillnader.\nSärskilt när det kommer till herrklippning, fades och skägg. I det här inlägget reder vi ut vad som skiljer en barber från en frisör,\noch hur du vet vad som passar dig bäst.",
    faq: [
      { q: "Vad är skillnaden mellan barber och frisör?", a: "Den största skillnaden är specialisering. En barber fokuserar på herrklippning, fades och skägg, medan en frisör jobbar med både dam och herr samt färg och styling." },
      { q: "Är barber bättre än frisör för herrklippning?", a: "För korta frisyrer, fades och skägg är barber oftast ett bättre val eftersom utbildning och teknik är anpassad specifikt för män." },
      { q: "Vad gör man på en barbershop?", a: "På en barbershop kan du klippa håret, forma skägget, trimma konturer och få traditionell rakning. Fokus ligger på precision och helhetsupplevelse." },
      { q: "Kan man gå till barber om man har längre hår?", a: "Ja, men barbershops är främst specialiserade på korta till medellånga frisyrer och skägg." },
      { q: "Vad är en barbershop?", a: "En salong specialiserad på herrklippning och skäggvård. Ofta kombineras traditionellt hantverk med moderna tekniker." },
      { q: "Är barbershop bara för män?", a: "Barbershops riktar sig främst till män, men fokus är herrgrooming." },
      { q: "Vad kostar det att gå till barber jämfört med frisör?", a: "Priset är ofta likvärdigt, men hos barber får du mer tid för detaljer som fades och skägg." },
      { q: "När ska man välja barber istället för frisör?", a: "Välj barber för exakt fade, välformade konturer eller regelbunden skäggtrimning. Frisör passar bättre för färgning och längre frisyrer." },
    ],
  },
  {
    slug: "barberare-eller-frisor-vad-ar-bast-for-man-i-stockholm",
    title: "Barberare eller frisör – vad är bäst för män i Stockholm?",
    metaDescription:
      "Ska du välja barberare eller vanlig frisör? Här jämför vi skillnaderna för att hjälpa dig hitta rätt val i Stockholm.",
    category: "Herrklippning",
    publishedAt: "2026-02-22",
    hero: ogFallback,
    excerpt:
      "Barberare eller frisör — vilket val passar dig som man i Stockholm?",
    intro: "Att välja mellan barberare och frisör kan kännas självklart för vissa och förvirrande för andra. Speciellt i en stad som Stockholm där utbudet är stort och kvaliteten varierar.",
    faq: [
      { q: "Vad är egentligen skillnaden mellan en barberare och en frisör?", a: "En barberare är specialiserad på herrklippning, fades, maskinteknik och skägg. En frisör har bredare utbildning och jobbar med både män och kvinnor, inklusive färgning." },
      { q: "Är barberare bättre på fades än frisörer?", a: "Generellt ja. En barberare arbetar dagligen med skin fades och taper fades, vilket gör tekniken och precisionen mer utvecklad." },
      { q: "Ska jag gå till barberare om jag inte har skägg?", a: "Absolut. En barberare är expert på herrklippningar oavsett om du har skägg eller inte." },
      { q: "Är det dyrare att gå till barberare i Stockholm?", a: "Priserna varierar men ligger ofta 400–600 kr. Det viktigaste är kvaliteten, upplevelsen och hur länge klippningen håller formen." },
      { q: "Hur ofta bör män klippa sig?", a: "Skin fade: var 2–3 vecka. Klassisk herrklippning: var 3–5 vecka. Skäggtrimning: var 2–4 vecka." },
      { q: "Vad ska jag välja om jag vill ha både hår och skägg fixat?", a: "Barberare. En kombinationsbehandling skapar balans mellan hår och ansiktsform." },
      { q: "Hur vet jag om en barberare är bra?", a: "Titta på före- och efterbilder, recensioner och om skägget harmoniserar med frisyren. En skicklig barberare lämnar inga hårda linjer eller ojämna övergångar." },
      { q: "Var hittar jag en riktigt bra barberare i Stockholm?", a: "Välj en salong som fokuserar 100% på män. Tailor's Barber arbetar dagligen med skin fades, taper fades och skäggformning." },
    ],
  },
  {
    slug: "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar-egentligen",
    title: "Vad kostar en bra barberare i Stockholm – och vad ingår egentligen?",
    metaDescription:
      "Hur mycket kostar en bra barberare i Stockholm? Vi går igenom vanliga priser för herrklippning, fade och skäggtrimning – samt vad som faktiskt ingår.",
    category: "Herrklippning",
    publishedAt: "2026-02-11",
    hero: ogFallback,
    excerpt:
      "Vad kostar en barberare i Stockholm — och vad ingår egentligen i priset?",
    intro: "Priset för en barberare i Stockholm kan variera ganska mycket, och det är inte alltid helt tydligt vad som faktiskt ingår. Därför är det klokt att titta på helheten, inte bara siffran i bokningssystemet.",
    faq: [],
  },
  {
    slug: "frisyr-sjalvfortroende",
    title: "Så påverkar din frisyr ditt självförtroende",
    metaDescription:
      "Din frisyr påverkar mer än du tror. Läs varför en bra klippning och ett vältrimmat skägg kan ge ett starkare intryck och mer självsäkerhet.",
    category: "Herrklippning",
    publishedAt: "2026-01-27",
    hero: ogFallback,
    excerpt:
      "Din frisyr säger mer om dig än du tror – och påverkar hur du känner dig inifrån och ut.",
    intro: "Se fräschare ut. Känn dig mer självsäker.",
    faq: [
      {
        q: "Hur ofta bör man klippa sig hos barberaren?",
        a: "Var fjärde till sjätte vecka är en bra riktlinje för de flesta klippningar. Fades och mer tekniska klippningar håller ofta kortare tid och kan behöva ses över var tredje till fjärde vecka.",
      },
      {
        q: "Kan en frisyr verkligen påverka självförtroende?",
        a: "Ja. Studier visar att utseende påverkar självkänsla, och frisyren är en av de faktorer vi har störst kontroll över. Att se ut som en version av dig själv du trivs med sätter tonen för hela dagen.",
      },
    ],
  },
  {
    slug: "hur-valjer-man-ratt-barberare",
    title: "Hur väljer man rätt barberare i Stockholm?",
    metaDescription:
      "Så väljer du rätt barberare i Stockholm – tips baserade på stil, erfarenhet, recensioner och vilken typ av klippning du vill ha.",
    category: "Herrklippning",
    publishedAt: "2026-02-09",
    hero: ogFallback,
    excerpt:
      "Att välja rätt barberare i Stockholm handlar om mer än pris och avstånd – här är vad som faktiskt spelar roll.",
    intro: "Att välja barberare i Stockholm kan kännas svårare än man tror. Det finns gott om barbershops, snygga bilder på Instagram och många som säger att de är “bäst”. Samtidigt märker många att resultatet inte alltid blir som man tänkt sig när man väl sitter i stolen.",
    faq: [
      {
        q: "Hur hittar man en bra barberare i Stockholm?",
        a: "Börja med personliga rekommendationer, kolla recensioner på Google och Bokadirekt, och titta på barnberarens Instagram för att se exempel på deras arbete. Prioritera en barberare vars stil matchar det du söker.",
      },
      {
        q: "Vad ska man tänka på när man väljer barberare?",
        a: "Erfarenhet, specialisering, kommunikation och recensioner är de viktigaste faktorerna. Ha gärna ett referensfoto redo och var tydlig med vad du vill ha vid bokningstillfället.",
      },
      {
        q: "Hur vet man att en barberare är bra på fades?",
        a: "Kolla deras sociala medier – en barberare som är skicklig på fades visar det gärna upp. Läs även recensioner specifikt om fades och graderade klippningar.",
      },
    ],
  },
  {
    slug: "vad-ska-man-saga-till-barberaren-fade",
    title: "Vad ska man säga till barberaren om man vill ha en fade?",
    metaDescription:
      "Fusklappen inför barberaren: de fyra fade-typerna (low, mid, high, taper) och exakt vad du ska säga för att få rätt klippning varje gång.",
    category: "Herrklippning" as BlogCategory,
    publishedAt: "2026-03-08",
    hero: midSkinFadeSidaAsset.url,
    excerpt:
      "Enkelt förklarat: de fyra vanligaste fade-typerna och vad du ska säga för att få exakt det resultatet du vill ha.",
    intro: "ATT BE OM EN FADE ÄR ENKELT – OM DU VET VAD DU SKA SÄGA TILL DIN BARBERARE.",
    faq: [
      { q: "Hur lång tid tar det att klippa en fade?", a: "En fade kräver precision och tar oftast mellan 30 och 45 minuter beroende på hur detaljerad övergången ska vara och vilken frisyr du har på toppen." },
      { q: "Vilken fade passar bäst för första gången?", a: "Om du aldrig haft en fade tidigare är en mid fade ofta ett bra alternativ. Den ger en tydlig övergång utan att bli för extrem." },
      { q: "Hur ofta behöver man klippa en fade?", a: "Fade-klippningar håller oftast bäst i två till fyra veckor. Sidorna är korta och utväxten syns snabbare." },
      { q: "Kan alla hårtyper ha en fade?", a: "Ja, fade fungerar på de flesta hårtyper. Tekniken kan behöva anpassas beroende på om håret är tjockt, tunt, rakt eller lockigt." },
      { q: "Vad är skillnaden mellan en fade och en taper?", a: "En fade innebär att håret gradvis går ner till mycket kort längd. En taper är mer diskret och sker främst runt nacken och öronen." },
      { q: "Hur vet jag vilken fade som passar mig bäst?", a: "Det beror på din ansiktsform, hårtyp och vilken stil du vill ha. En barberare kan hjälpa dig avgöra om en low, mid eller high fade passar bäst." },
    ],
  },
];

export const articleBySlug: Record<string, BlogArticle> = Object.fromEntries(
  articles.map((a) => [a.slug, a]),
);

export const BOOKING_URL_ALL =
  "https://www.bokadirekt.se/places/tailors-barber-stockholm-ahlens-city-37678";
